import { useState, useEffect } from 'react'
import {
  X, Key, Zap, Check, AlertCircle, Languages, RotateCcw, Trash2, Gift, Star, Sparkles,
  Bot, CreditCard, Github, Settings, Globe, Image, Search, ChevronRight, Info, AlertTriangle,
  Shuffle, Crown, Coins, Scale, Gem, Loader2, Play, Bug, Copy, HelpCircle, ExternalLink
} from 'lucide-react'
import { copyDebugState } from '@utils/debugState'
import { useStore } from '@store/useStore'
import { getApiKey, setApiKey, isProviderConfigured, getAvailableProviders, PROVIDERS, isDevMode, toggleDebugMode } from '@services/llm'
import { generateImage } from '@services/imagen'
import { getCredits, setCredits, initDemoCredits, CREDIT_PACKS } from '@services/stripe'
import { isConfigured as isPerplexityConfigured } from '@services/perplexity'
import { getAvailableSearchProviders, SEARCH_PROVIDERS } from '@services/search'
import { PRESETS, getPresetConfig, getRandomAntiFingerprint, validateConfig } from '@config/llmPresets'
import PromoCode from './PromoCode'
import AdminMessage from './AdminMessage'

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
]

const TABS = [
  { id: 'keys', label: 'Clés API', icon: Key },
  { id: 'models', label: 'Modèles', icon: Bot },
  { id: 'account', label: 'Compte', icon: CreditCard },
  { id: 'github', label: 'GitHub', icon: Github },
  { id: 'settings', label: 'Paramètres', icon: Settings },
]

// Model configurations with recommendations
const TEXT_MODELS = {
  claude: [
    { id: 'sonnet', label: 'Sonnet 4.5', badge: '⭐ Recommandé', recommended: true },
    { id: 'opus', label: 'Opus 4.5', badge: '💰 Premium' },
    { id: 'haiku', label: 'Haiku', badge: '⚡ Rapide' },
    { id: 'sonnet-4', label: 'Sonnet 4', badge: '⚠️ Legacy', legacy: true },
  ],
  openai: [
    { id: 'gpt-5.1', label: 'GPT-5.1', badge: '⭐ Recommandé', recommended: true },
    { id: 'gpt-5', label: 'GPT-5', badge: '' },
    { id: 'gpt-4o', label: 'GPT-4o', badge: '' },
    { id: 'gpt-4o-mini', label: 'GPT-4o Mini', badge: '⚡ Budget' },
  ],
  gemini: [
    { id: 'pro', label: 'Gemini 3.0 Pro', badge: '⭐ Recommandé', recommended: true },
    { id: 'deep-think', label: 'Deep Think', badge: '🧠 Raisonnement' },
    { id: 'flash', label: 'Flash 2.5', badge: '⚡ Rapide' },
    { id: 'flash-lite', label: 'Flash Lite', badge: '💰 Budget' },
  ],
}

// Image provider options (prices updated Dec 2025)
const IMAGE_PROVIDERS = {
  dalle: {
    name: 'GPT-Image-1',
    provider: 'OpenAI',
    description: 'Haute qualité, texte lisible',
    cost: '$0.01-0.17/image', // low/medium/high quality
    badge: 'Premium',
    badgeColor: 'amber',
  },
  gemini: {
    name: 'Gemini Flash 2.0',
    provider: 'Google',
    description: 'Génération native, économique',
    cost: '~$0.04/image',
    badge: 'Budget',
    badgeColor: 'emerald',
  },
}

export default function SettingsModal({ onClose }) {
  const { settings, setSettings, credits, setCredits: setStoreCredits } = useStore()
  const [activeTab, setActiveTab] = useState('keys')

  // API Keys state
  const [keys, setKeys] = useState({
    claude: getApiKey('claude') || '',
    openai: getApiKey('openai') || '',
    gemini: getApiKey('gemini') || '',
    perplexity: getApiKey('perplexity') || '',
    github: getApiKey('github') || '',
  })

  const [saved, setSaved] = useState({})
  const [language, setLanguage] = useState(localStorage.getItem('emd_language') || 'fr')

  const handleLanguageChange = (lang) => {
    setLanguage(lang)
    localStorage.setItem('emd_language', lang)
  }

  const handleKeyChange = (provider, value) => {
    setKeys((prev) => ({ ...prev, [provider]: value }))
  }

  const handleKeySave = (provider) => {
    setApiKey(provider, keys[provider])
    setSaved((prev) => ({ ...prev, [provider]: true }))
    setTimeout(() => setSaved((prev) => ({ ...prev, [provider]: false })), 2000)
  }

  const handleAddDemoCredits = () => {
    const newCredits = credits + 50
    setCredits(newCredits)
    setStoreCredits(newCredits)
  }

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'keys':
        return <TabKeys keys={keys} saved={saved} onKeyChange={handleKeyChange} onKeySave={handleKeySave} />
      case 'models':
        return <TabModels settings={settings} setSettings={setSettings} />
      case 'account':
        return <TabAccount credits={credits} onAddDemoCredits={handleAddDemoCredits} />
      case 'github':
        return <TabGitHub keys={keys} saved={saved} onKeyChange={handleKeyChange} onKeySave={handleKeySave} />
      case 'settings':
        return <TabSettings language={language} onLanguageChange={handleLanguageChange} />
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl border border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary-400" />
            Paramètres
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content with sidebar */}
        <div className="flex h-[calc(85vh-73px)]">
          {/* Sidebar */}
          <nav className="w-48 bg-slate-950/50 border-r border-slate-800 p-3 flex-shrink-0">
            <ul className="space-y-1">
              {TABS.map(tab => (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary-500/20 text-primary-400 border-l-2 border-primary-500'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {activeTab === tab.id && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Admin announcement */}
            <AdminMessage onNavigate={setActiveTab} />

            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============ TAB: CLÉS API ============
function TabKeys({ keys, saved, onKeyChange, onKeySave }) {
  // Image test state
  const [imageTest, setImageTest] = useState({
    dalle: { loading: false, result: null },
    gemini: { loading: false, result: null },
  })

  // Test image generation for a specific provider
  const testImageProvider = async (provider) => {
    setImageTest(prev => ({
      ...prev,
      [provider]: { loading: true, result: null }
    }))

    try {
      console.log(`🧪 Testing ${provider} image generation...`)
      const result = await generateImage(
        'A professional French artisan at work, clean modern workspace, natural lighting',
        { provider, width: 512, height: 512 }
      )
      console.log(`🧪 ${provider} test success!`)
      setImageTest(prev => ({
        ...prev,
        [provider]: { loading: false, result: { success: true, url: result.url } }
      }))
    } catch (err) {
      console.error(`🧪 ${provider} test failed:`, err)
      setImageTest(prev => ({
        ...prev,
        [provider]: {
          loading: false,
          result: {
            success: false,
            error: err.message,
            // Capture enhanced error properties if available
            code: err.code,
            title: err.title,
            action: err.action,
            actionUrl: err.actionUrl,
            note: err.note,
            fallback: err.fallback,
          }
        }
      }))
    }
  }

  const KeyInput = ({ provider, label, placeholder, description, required }) => (
    <div className="flex gap-3">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-sm font-medium text-slate-300">{label}</label>
          {required && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-medium">
              Requis
            </span>
          )}
          {isProviderConfigured(provider) && (
            <Check className="w-4 h-4 text-green-400" />
          )}
        </div>
        <input
          type="password"
          value={keys[provider]}
          onChange={(e) => onKeyChange(provider, e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50"
        />
        {description && (
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        )}
      </div>
      <button
        onClick={() => onKeySave(provider)}
        className={`self-end px-4 py-2.5 rounded-lg transition-all font-medium ${
          saved[provider]
            ? 'bg-green-600 text-white'
            : 'bg-slate-700 hover:bg-slate-600 text-white'
        }`}
      >
        {saved[provider] ? <Check className="w-4 h-4" /> : 'Save'}
      </button>
    </div>
  )

  return (
    <div className="space-y-8">
      {/* Text Generation */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-lg">Génération de texte</h3>
          <span className="text-xs text-slate-500">(au moins 1 requis)</span>
        </div>
        <div className="space-y-4 pl-7">
          <KeyInput
            provider="claude"
            label="Anthropic (Claude)"
            placeholder="sk-ant-api03-..."
            description="Recommandé. Meilleure qualité de génération."
          />
          <KeyInput
            provider="openai"
            label="OpenAI (GPT)"
            placeholder="sk-proj-..."
          />
          <KeyInput
            provider="gemini"
            label="Google (Gemini)"
            placeholder="AIzaSy..."
            description="Aussi utilisé pour la génération d'images."
          />
        </div>
      </section>

      {/* SERP Analysis */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-lg">Recherche Web</h3>
          <span className="text-xs text-slate-500">(optionnel)</span>
        </div>
        <div className="space-y-4 pl-7">
          <KeyInput
            provider="perplexity"
            label="Perplexity"
            placeholder="pplx-..."
            description="Meilleure qualite de recherche. Sinon Claude/GPT/Gemini font aussi du search."
          />
        </div>
      </section>

      {/* Image Generation */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Image className="w-5 h-5 text-purple-400" />
          <h3 className="font-semibold text-lg">Génération d'images</h3>
        </div>
        <div className="pl-7 space-y-3">
          {/* OpenAI GPT-Image */}
          <div className={`bg-slate-800/50 rounded-lg p-4 border ${keys.openai ? 'border-amber-500/30' : 'border-slate-700'}`}>
            <div className="flex items-start gap-3">
              <Sparkles className={`w-5 h-5 flex-shrink-0 mt-0.5 ${keys.openai ? 'text-amber-400' : 'text-slate-500'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-300 font-medium">GPT-Image-1</p>
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">Premium</span>
                  {keys.openai && <Check className="w-3 h-3 text-green-400" />}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Utilise votre clé OpenAI. Haute qualité, texte lisible ($0.01-0.17/image)
                </p>
                {/* Test button and result */}
                {keys.openai && (
                  <div className="mt-2">
                    <button
                      onClick={() => testImageProvider('dalle')}
                      disabled={imageTest.dalle.loading}
                      className="px-3 py-1.5 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 rounded-lg text-xs font-medium text-amber-400 transition-all flex items-center gap-1.5"
                    >
                      {imageTest.dalle.loading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Test en cours...</>
                      ) : (
                        <><Play className="w-3 h-3" /> Tester</>
                      )}
                    </button>
                    {imageTest.dalle.result && (
                      <div className="mt-2">
                        {imageTest.dalle.result.success ? (
                          <div className="space-y-1">
                            <p className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> OK!</p>
                            <img src={imageTest.dalle.result.url} alt="Test" className="w-20 h-20 object-cover rounded border border-slate-600" />
                          </div>
                        ) : (
                          <div className="space-y-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                            <p className="text-xs text-red-400">{imageTest.dalle.result.error}</p>
                            {imageTest.dalle.result.actionUrl && (
                              <a
                                href={imageTest.dalle.result.actionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 rounded text-[10px] text-amber-400 font-medium transition-all"
                              >
                                {imageTest.dalle.result.action || 'Résoudre'}
                                <ChevronRight className="w-3 h-3" />
                              </a>
                            )}
                            {imageTest.dalle.result.note && (
                              <p className="text-[10px] text-slate-500">{imageTest.dalle.result.note}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Gemini Imagen */}
          <div className={`bg-slate-800/50 rounded-lg p-4 border ${keys.gemini ? 'border-emerald-500/30' : 'border-slate-700'}`}>
            <div className="flex items-start gap-3">
              <Sparkles className={`w-5 h-5 flex-shrink-0 mt-0.5 ${keys.gemini ? 'text-emerald-400' : 'text-slate-500'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-300 font-medium">Gemini Flash 2.0</p>
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">Budget</span>
                  {keys.gemini && <Check className="w-3 h-3 text-green-400" />}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Utilise votre clé Google. Génération native (~$0.04/image)
                </p>
                {/* Test button and result */}
                {keys.gemini && (
                  <div className="mt-2">
                    <button
                      onClick={() => testImageProvider('gemini')}
                      disabled={imageTest.gemini.loading}
                      className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 rounded-lg text-xs font-medium text-emerald-400 transition-all flex items-center gap-1.5"
                    >
                      {imageTest.gemini.loading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Test en cours...</>
                      ) : (
                        <><Play className="w-3 h-3" /> Tester</>
                      )}
                    </button>
                    {imageTest.gemini.result && (
                      <div className="mt-2">
                        {imageTest.gemini.result.success ? (
                          <div className="space-y-1">
                            <p className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> OK!</p>
                            <img src={imageTest.gemini.result.url} alt="Test" className="w-20 h-20 object-cover rounded border border-slate-600" />
                          </div>
                        ) : (
                          <div className="space-y-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                            <p className="text-xs text-red-400">{imageTest.gemini.result.error}</p>
                            {imageTest.gemini.result.actionUrl && (
                              <a
                                href={imageTest.gemini.result.actionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 rounded text-[10px] text-emerald-400 font-medium transition-all"
                              >
                                {imageTest.gemini.result.action || 'Résoudre'}
                                <ChevronRight className="w-3 h-3" />
                              </a>
                            )}
                            {imageTest.gemini.result.note && (
                              <p className="text-[10px] text-slate-500">{imageTest.gemini.result.note}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info note */}
          {!keys.openai && !keys.gemini && (
            <div className="flex items-start gap-2 p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                Configurez au moins une clé OpenAI ou Gemini pour générer des images
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Note */}
      <div className="flex items-start gap-2 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
        <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          Les clés sont stockées localement dans votre navigateur (localStorage) et ne sont jamais envoyées à nos serveurs.
        </p>
      </div>
    </div>
  )
}

// ============ TAB: MODÈLES ============
function TabModels({ settings, setSettings }) {
  const currentProvider = settings.provider || 'claude'
  const currentModel = settings.model || 'sonnet'
  const useDualModel = settings.useDualModel || false
  const reasoningModel = settings.reasoningModel || 'sonnet'
  const productionModel = settings.productionModel || 'opus'
  const [selectedPreset, setSelectedPreset] = useState(null)
  const availableProviders = getAvailableProviders()

  // Preset icons
  const PRESET_ICONS = {
    economique: Coins,
    equilibre: Scale,
    qualite: Gem,
    maximum: Crown,
  }

  // Map presets to image providers
  const PRESET_IMAGE_PROVIDER = {
    economique: 'gemini',  // Budget = Gemini Imagen
    equilibre: 'auto',     // Balanced = Auto (best available)
    qualite: 'dalle',      // Quality = GPT-Image-1
    maximum: 'dalle',      // Premium = GPT-Image-1
  }

  // Apply a preset
  const applyPreset = (presetId) => {
    const config = getPresetConfig(presetId, currentProvider)
    if (config) {
      setSettings({
        useDualModel: true,
        reasoningModel: config.reasoningModel,
        productionModel: config.productionModel,
        imageProvider: PRESET_IMAGE_PROVIDER[presetId] || 'auto',
      })
      setSelectedPreset(presetId)
    }
  }

  // Apply anti-fingerprint random config
  const applyAntiFingerprint = () => {
    const combo = getRandomAntiFingerprint(availableProviders)
    if (combo) {
      setSettings({
        useDualModel: true,
        provider: combo.production.provider,
        reasoningModel: combo.reasoning.model,
        productionModel: combo.production.model,
        // Store reasoning provider separately for cross-provider mode
        reasoningProvider: combo.reasoning.provider,
        productionProvider: combo.production.provider,
        // Also rotate image providers for full anti-fingerprint
        imageProvider: 'rotate',
      })
      setSelectedPreset('anti-fingerprint')
    }
  }

  // Model selector component for reuse
  const ModelSelector = ({ value, onChange, label, description, highlightColor = 'primary' }) => (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-sm text-slate-400">{label}</label>
        {description && (
          <span className="text-xs text-slate-500">({description})</span>
        )}
      </div>
      <div className="space-y-2">
        {TEXT_MODELS[currentProvider]?.map(model => (
          <button
            key={model.id}
            onClick={() => onChange(model.id)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
              value === model.id
                ? highlightColor === 'amber'
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-primary-500 bg-primary-500/10'
                : model.legacy
                  ? 'border-slate-700/50 hover:border-slate-600 opacity-60'
                  : 'border-slate-700 hover:border-slate-600'
            }`}
          >
            <span className={`font-medium ${
              value === model.id
                ? highlightColor === 'amber' ? 'text-amber-400' : 'text-primary-400'
                : 'text-slate-300'
            }`}>
              {model.label}
            </span>
            {model.badge && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                model.recommended
                  ? 'bg-primary-500/20 text-primary-400'
                  : model.legacy
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-slate-700 text-slate-400'
              }`}>
                {model.badge}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )

  // Validate current config
  const validation = validateConfig({ reasoningModel, productionModel })

  return (
    <div className="space-y-8">
      {/* Quick Presets */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-lg">Configuration rapide</h3>
        </div>

        {/* Preset cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {Object.entries(PRESETS).map(([id, preset]) => {
            const Icon = PRESET_ICONS[id] || Star
            const isSelected = selectedPreset === id
            const badgeColors = {
              emerald: 'bg-emerald-500/20 text-emerald-400',
              primary: 'bg-primary-500/20 text-primary-400',
              amber: 'bg-amber-500/20 text-amber-400',
              purple: 'bg-purple-500/20 text-purple-400',
            }
            return (
              <button
                key={id}
                onClick={() => applyPreset(id)}
                className={`p-4 rounded-xl border text-left transition-all hover:scale-[1.02] ${
                  isSelected
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-primary-400' : 'text-slate-400'}`} />
                  <span className="font-semibold">{preset.name}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{preset.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${badgeColors[preset.badgeColor]}`}>
                    {preset.badge}
                  </span>
                  <span className="text-xs text-slate-500">{preset.costRange}</span>
                </div>
                {/* Image provider indicator */}
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-700/50">
                  <Image className="w-3 h-3 text-slate-500" />
                  <span className="text-[10px] text-slate-500">
                    {PRESET_IMAGE_PROVIDER[id] === 'gemini' ? 'Gemini' : PRESET_IMAGE_PROVIDER[id] === 'dalle' ? 'GPT-Image' : 'Auto'}
                  </span>
                </div>
              </button>
            )
          })}
        </div>

        {/* Anti-fingerprint button */}
        {availableProviders.length >= 2 && (
          <button
            onClick={applyAntiFingerprint}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              selectedPreset === 'anti-fingerprint'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-slate-700 hover:border-purple-500/50 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg">
                <Shuffle className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-200">Mode Anti-Fingerprint</p>
                <p className="text-xs text-slate-500">
                  Mix aléatoire des providers pour varier le style d'écriture
                </p>
              </div>
              <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                PBN Safe
              </span>
            </div>
          </button>
        )}
      </section>

      {/* Provider selection */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-lg">Provider principal</h3>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {Object.entries(PROVIDERS).map(([key, provider]) => (
            <button
              key={key}
              onClick={() => setSettings({ provider: key })}
              className={`px-4 py-3 rounded-lg border transition-all ${
                currentProvider === key
                  ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                  : 'border-slate-700 hover:border-slate-600 text-slate-300'
              }`}
            >
              <span className="font-medium">{provider.name}</span>
              {isProviderConfigured(key) && (
                <Check className="w-4 h-4 inline ml-2 text-green-400" />
              )}
            </button>
          ))}
        </div>

        {/* Dual-model toggle */}
        <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary-500/20 to-amber-500/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-slate-200">Mode dual-modèle</p>
                <p className="text-xs text-slate-500">
                  Modèle économique pour l'analyse, premium pour la production
                </p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ useDualModel: !useDualModel })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                useDualModel ? 'bg-primary-500' : 'bg-slate-600'
              }`}
            >
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                useDualModel ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
        </div>
      </section>

      {/* Model selection */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-lg">Modèles</h3>
        </div>

        {useDualModel ? (
          <div className="space-y-6">
            {/* Reasoning model */}
            <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-primary-500/20 rounded-lg">
                  <Bot className="w-4 h-4 text-primary-400" />
                </div>
                <span className="font-medium text-primary-400">Raisonnement</span>
                <span className="text-xs text-slate-500 ml-auto">SERP → DA</span>
              </div>
              <ModelSelector
                value={reasoningModel}
                onChange={(model) => { setSettings({ reasoningModel: model }); setSelectedPreset(null) }}
                label="Modèle pour l'analyse"
                description="6 phases"
                highlightColor="primary"
              />
            </div>

            {/* Production model */}
            <div className="p-4 bg-slate-800/30 rounded-xl border border-amber-500/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-amber-500/20 rounded-lg">
                  <Star className="w-4 h-4 text-amber-400" />
                </div>
                <span className="font-medium text-amber-400">Production</span>
                <span className="text-xs text-slate-500 ml-auto">Content + Images</span>
              </div>
              <ModelSelector
                value={productionModel}
                onChange={(model) => { setSettings({ productionModel: model }); setSelectedPreset(null) }}
                label="Modèle pour la génération"
                description="HTML final"
                highlightColor="amber"
              />
            </div>

            {/* Validation warnings */}
            {validation.warnings.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-300">
                  {validation.warnings.map((w, i) => (
                    <p key={i}>{w}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <ModelSelector
            value={currentModel}
            onChange={(model) => setSettings({ model: model })}
            label="Modèle"
            highlightColor="primary"
          />
        )}
      </section>

      {/* SERP Analysis - Multi-provider */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-lg">Recherche Web (SERP)</h3>
        </div>

        {/* Search provider selector */}
        <div className="space-y-3">
          {/* Auto mode */}
          <button
            onClick={() => setSettings({ searchProvider: 'auto' })}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              settings.searchProvider === 'auto'
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-blue-400" />
              <div className="flex-1">
                <p className="font-medium text-slate-200">Auto (recommande)</p>
                <p className="text-xs text-slate-500">Utilise le meilleur provider disponible</p>
              </div>
              {settings.searchProvider === 'auto' && (
                <Check className="w-4 h-4 text-blue-400" />
              )}
            </div>
          </button>

          {/* Multi/Random mode */}
          <button
            onClick={() => setSettings({ searchProvider: 'multi' })}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              settings.searchProvider === 'multi'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <Shuffle className="w-5 h-5 text-purple-400" />
              <div className="flex-1">
                <p className="font-medium text-slate-200">Multi (anti-fingerprint)</p>
                <p className="text-xs text-slate-500">Rotation aleatoire entre providers</p>
              </div>
              {settings.searchProvider === 'multi' && (
                <Check className="w-4 h-4 text-purple-400" />
              )}
            </div>
          </button>

          {/* Individual providers */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            {Object.entries(SEARCH_PROVIDERS).map(([key, provider]) => {
              const isAvailable = getAvailableSearchProviders().includes(key)
              const isSelected = settings.searchProvider === key
              return (
                <button
                  key={key}
                  onClick={() => isAvailable && setSettings({ searchProvider: key })}
                  disabled={!isAvailable}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500/10'
                      : isAvailable
                        ? 'border-slate-700 hover:border-slate-600'
                        : 'border-slate-700/50 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium text-sm ${isSelected ? 'text-blue-400' : 'text-slate-300'}`}>
                      {provider.name}
                    </span>
                    {isAvailable && <Check className="w-3 h-3 text-green-400" />}
                  </div>
                  <p className="text-xs text-slate-500">{provider.description}</p>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Image Generation - Provider Selection */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Image className="w-5 h-5 text-purple-400" />
          <h3 className="font-semibold text-lg">Génération d'images</h3>
        </div>

        <div className="space-y-3">
          {/* Auto mode */}
          <button
            onClick={() => setSettings({ imageProvider: 'auto' })}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              (settings.imageProvider || 'auto') === 'auto'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-purple-400" />
              <div className="flex-1">
                <p className="font-medium text-slate-200">Auto (recommandé)</p>
                <p className="text-xs text-slate-500">Utilise le meilleur provider disponible</p>
              </div>
              {(settings.imageProvider || 'auto') === 'auto' && (
                <Check className="w-4 h-4 text-purple-400" />
              )}
            </div>
          </button>

          {/* Anti-fingerprint rotation */}
          <button
            onClick={() => setSettings({ imageProvider: 'rotate' })}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              settings.imageProvider === 'rotate'
                ? 'border-pink-500 bg-pink-500/10'
                : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
            }`}
          >
            <div className="flex items-center gap-3">
              <Shuffle className="w-5 h-5 text-pink-400" />
              <div className="flex-1">
                <p className="font-medium text-slate-200">Anti-fingerprint</p>
                <p className="text-xs text-slate-500">Rotation aléatoire entre providers</p>
              </div>
              <span className="text-xs px-2 py-1 bg-pink-500/20 text-pink-400 rounded-full">
                PBN Safe
              </span>
            </div>
          </button>

          {/* Individual providers */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            {Object.entries(IMAGE_PROVIDERS).map(([key, provider]) => {
              const isConfigured = key === 'dalle' ? isProviderConfigured('openai') : isProviderConfigured('gemini')
              const isSelected = settings.imageProvider === key
              const badgeColors = {
                amber: 'bg-amber-500/20 text-amber-400',
                emerald: 'bg-emerald-500/20 text-emerald-400',
              }
              return (
                <button
                  key={key}
                  onClick={() => isConfigured && setSettings({ imageProvider: key })}
                  disabled={!isConfigured}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    isSelected
                      ? key === 'dalle'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-emerald-500 bg-emerald-500/10'
                      : isConfigured
                        ? 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                        : 'border-slate-700/50 opacity-50 cursor-not-allowed bg-slate-800/20'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${
                      isSelected
                        ? key === 'dalle' ? 'text-amber-400' : 'text-emerald-400'
                        : 'text-slate-300'
                    }`}>
                      {provider.name}
                    </span>
                    {isConfigured && <Check className="w-3 h-3 text-green-400" />}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{provider.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${badgeColors[provider.badgeColor]}`}>
                      {provider.badge}
                    </span>
                    <span className="text-[10px] text-slate-500">{provider.cost}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Image Quality Selection */}
          <div className="pt-4 mt-4 border-t border-slate-700/50">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-slate-300">Qualité d'image</span>
              <span className="text-[10px] text-slate-500">(lié au preset LLM)</span>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[
                { id: 'auto', label: 'Auto', desc: 'Suit le preset', color: 'slate' },
                { id: 'budget', label: 'Budget', desc: 'Low/Fast', color: 'emerald', cost: '~$0.01-0.02' },
                { id: 'balanced', label: 'Standard', desc: 'Medium', color: 'blue', cost: '~$0.03-0.05' },
                { id: 'quality', label: 'Premium', desc: 'High/Ultra', color: 'amber', cost: '~$0.15-0.18' },
              ].map(tier => {
                const isSelected = (settings.imageQuality || 'auto') === tier.id
                const colorClasses = {
                  slate: 'border-slate-500 bg-slate-500/10 text-slate-400',
                  emerald: 'border-emerald-500 bg-emerald-500/10 text-emerald-400',
                  blue: 'border-blue-500 bg-blue-500/10 text-blue-400',
                  amber: 'border-amber-500 bg-amber-500/10 text-amber-400',
                }
                return (
                  <button
                    key={tier.id}
                    onClick={() => setSettings({ imageQuality: tier.id })}
                    className={`p-2 rounded-lg border text-center transition-all ${
                      isSelected
                        ? colorClasses[tier.color]
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                    }`}
                  >
                    <p className={`text-xs font-medium ${isSelected ? '' : 'text-slate-300'}`}>{tier.label}</p>
                    <p className="text-[10px] text-slate-500">{tier.desc}</p>
                    {tier.cost && <p className="text-[9px] text-slate-600 mt-0.5">{tier.cost}</p>}
                  </button>
                )
              })}
            </div>

            <p className="text-[10px] text-slate-500 mt-2">
              En mode "Auto", la qualité suit le preset LLM (Économique → Budget, Maximum → Premium)
            </p>
          </div>
        </div>
      </section>

      {/* Debug Mode Toggle */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Bug className="w-5 h-5 text-orange-400" />
          <h3 className="font-semibold text-lg">Mode debug</h3>
        </div>
        <DebugModeToggle />
      </section>
    </div>
  )
}

// Debug mode toggle component
function DebugModeToggle() {
  const { ui, setDebugMode } = useStore()
  const debugEnabled = ui.debugMode

  const handleToggle = () => {
    const newValue = !debugEnabled
    toggleDebugMode(newValue) // Update localStorage
    setDebugMode(newValue)    // Update store (triggers re-render everywhere)
  }

  return (
    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Bug className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <p className="font-medium text-slate-200">Afficher debug LLM</p>
            <p className="text-xs text-slate-500">
              Affiche les prompts et réponses dans la conversation et la console
            </p>
          </div>
        </div>
        <button
          onClick={handleToggle}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            debugEnabled ? 'bg-orange-500' : 'bg-slate-600'
          }`}
        >
          <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
            debugEnabled ? 'translate-x-6' : 'translate-x-0.5'
          }`} />
        </button>
      </div>
      {debugEnabled && (
        <p className="text-[10px] text-orange-400/70 mt-2">
          📤📥 Les prompts/réponses LLM apparaissent dans la conversation et la console (F12)
        </p>
      )}
    </div>
  )
}

// ============ TAB: COMPTE ============
function TabAccount({ credits, onAddDemoCredits }) {
  return (
    <div className="space-y-8">
      {/* Credits balance */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-lg">Solde</h3>
        </div>
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-6 border border-amber-500/20">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-4xl font-bold text-amber-400">{credits}</span>
              <span className="text-slate-400 ml-2 text-lg">crédits</span>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-slate-300">≈ {Math.floor(credits / 15)}</p>
              <p className="text-sm text-slate-500">sites complets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Buy credits */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-lg">Acheter des crédits</h3>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {CREDIT_PACKS.map((pack) => (
            <button
              key={pack.id}
              className={`relative px-5 py-5 rounded-xl text-left transition-all hover:scale-[1.02] ${
                pack.popular
                  ? 'bg-gradient-to-br from-primary-600/20 to-accent-600/20 border-2 border-primary-500/50'
                  : pack.bestValue
                    ? 'bg-gradient-to-br from-amber-600/20 to-orange-600/20 border-2 border-amber-500/50'
                    : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
              }`}
            >
              {pack.popular && (
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-primary-500 text-xs font-bold rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" /> Populaire
                </span>
              )}
              {pack.bestValue && (
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-xs font-bold rounded-full text-slate-900 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Meilleur prix
                </span>
              )}
              <div className="text-lg font-bold text-white mb-1">{pack.label}</div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-primary-400">{pack.priceLabel}</span>
                {pack.bonus && (
                  <span className="text-xs text-emerald-400 font-medium bg-emerald-500/10 px-2 py-0.5 rounded-full">
                    {pack.bonus}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-2">{pack.credits} crédits</div>
            </button>
          ))}
        </div>

        {/* Demo credits */}
        <button
          onClick={onAddDemoCredits}
          className="w-full mt-4 py-3 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 rounded-lg text-sm font-medium transition-colors text-amber-400"
        >
          + 50 crédits démo (gratuit)
        </button>
      </section>

      {/* Promo Code */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-emerald-400" />
          <h3 className="font-semibold text-lg">Code Promo</h3>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <PromoCode compact />
        </div>
      </section>
    </div>
  )
}

// ============ TAB: GITHUB ============
function TabGitHub({ keys, saved, onKeyChange, onKeySave }) {
  const isConfigured = !!keys.github

  return (
    <div className="space-y-8">
      {/* GitHub Token */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Github className="w-5 h-5 text-slate-300" />
          <h3 className="font-semibold text-lg">Token GitHub</h3>
        </div>

        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-medium text-slate-300">Personal Access Token</label>
                {isConfigured && <Check className="w-4 h-4 text-green-400" />}
              </div>
              <input
                type="password"
                value={keys.github}
                onChange={(e) => onKeyChange('github', e.target.value)}
                placeholder="ghp_..."
                className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50"
              />
            </div>
            <button
              onClick={() => onKeySave('github')}
              className={`self-end px-4 py-2.5 rounded-lg transition-all font-medium ${
                saved.github
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
            >
              {saved.github ? <Check className="w-4 h-4" /> : 'Save'}
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Comment créer un token :</h4>
            <ol className="text-xs text-slate-500 space-y-1 list-decimal list-inside">
              <li>Aller sur <span className="text-primary-400">github.com → Settings → Developer settings</span></li>
              <li>Cliquer sur <span className="text-slate-400">Personal access tokens → Tokens (classic)</span></li>
              <li>Generate new token avec les scopes : <code className="bg-slate-700 px-1 rounded">repo</code>, <code className="bg-slate-700 px-1 rounded">workflow</code></li>
              <li>Copier le token et le coller ci-dessus</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Deployment info */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-emerald-400" />
          <h3 className="font-semibold text-lg">Déploiement</h3>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-300 font-medium">GitHub Pages</p>
              <p className="text-xs text-slate-500 mt-1">
                Les sites générés sont automatiquement déployés sur GitHub Pages.
                Chaque site obtient une URL en <code className="bg-slate-700 px-1 rounded">username.github.io/repo-name</code>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

// ============ TAB: PARAMÈTRES ============
function TabSettings({ language, onLanguageChange }) {
  const [copied, setCopied] = useState(false)

  const handleCopyDebugState = async () => {
    const result = await copyDebugState()
    if (result.success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    }
  }

  return (
    <div className="space-y-8">
      {/* Language */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Languages className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-lg">Langue des sites générés</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => onLanguageChange(lang.code)}
              className={`px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                language === lang.code
                  ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                  : 'border-slate-700 hover:border-slate-600 text-slate-300'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="font-medium">{lang.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Reset options */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-slate-400" />
          <h3 className="font-semibold text-lg">Réinitialisation</h3>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => {
              localStorage.removeItem('emd_onboarded')
              window.location.reload()
            }}
            className="w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 border border-slate-700"
          >
            <RotateCcw className="w-4 h-4" />
            Relancer l'onboarding
          </button>

          <button
            onClick={() => {
              if (confirm('⚠️ Supprimer TOUTES les données locales ?\n\nCela inclut :\n- Clés API\n- Crédits\n- Projets sauvegardés\n- Préférences\n\nCette action est irréversible.')) {
                localStorage.clear()
                window.location.reload()
              }
            }}
            className="w-full py-3 px-4 bg-red-900/20 hover:bg-red-900/40 text-red-400 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 border border-red-900/50"
          >
            <Trash2 className="w-4 h-4" />
            Reset complet
            <AlertTriangle className="w-4 h-4 ml-2 opacity-60" />
          </button>
        </div>

        <p className="text-xs text-slate-500 mt-4 text-center">
          Le reset complet supprime toutes les données stockées dans votre navigateur.
        </p>
      </section>

      {/* Support section */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-lg">Support</h3>
        </div>

        <div className="space-y-3">
          {/* Copy debug state button */}
          <button
            onClick={handleCopyDebugState}
            className={`w-full py-3 px-4 rounded-lg text-sm transition-all flex items-center justify-center gap-2 border ${
              copied
                ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-400'
                : 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300'
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                État copié dans le presse-papier !
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copier l'état debug
              </>
            )}
          </button>

          {/* Support form link */}
          <a
            href="https://forms.gle/YOUR_FORM_ID_HERE"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 px-4 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 text-blue-400"
          >
            <ExternalLink className="w-4 h-4" />
            Contacter le support
          </a>

          <p className="text-xs text-slate-500 mt-2 text-center">
            Copiez l'état debug puis collez-le dans le formulaire de support pour nous aider à diagnostiquer votre problème.
          </p>
        </div>
      </section>
    </div>
  )
}
